import { useMutation, useQuery } from '@tanstack/react-query';
import { chatbotsService } from '../services/chatbots.service';
import { UpdateBasicInfoPayload, UpdateBasicInfoResponse, UpdateConfigPayload, UpdateConfigResponse, UpdateAppearancePayload, UpdateAppearanceResponse, DataFieldCreatePayload, CreateDataFieldResponse, GetDataFieldsResponse, DataFieldUpdatePayload, UpdateDataFieldResponse, DeleteDataFieldResponse, UploadLogoResponse, AiRoleResponse, DataColletionResponse, DataColletionPayload } from '../types/types';
import { useAuth } from '@/app/providers/AuthProvider';
import { ChatbotBuilderResponse } from '../types/chatbotbuilder.type';
import { usePermissions } from '@/hooks/usePermissions';

export const useChatbotData = (chatbotId: string | null) => {
  const { visitorId } = useAuth()
  const { hasPermission } = usePermissions()
  return useQuery<ChatbotBuilderResponse, Error>({
    queryKey: ['chatbotBuilder', chatbotId],
    queryFn: () => chatbotsService.getChatbotBuilder(chatbotId!, visitorId),
    enabled: !!chatbotId && hasPermission('chatbots.view'),
    staleTime: 0,
  });
};
export const useAiRole = (chatbotId: string | null) => {
  return useQuery<AiRoleResponse, Error>({
    queryKey: ['airesponse', chatbotId],
    queryFn: () => chatbotsService.getAiRole(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpdateBasicInfo = () => {
  return useMutation<UpdateBasicInfoResponse, Error, { chatbotId: string; data: UpdateBasicInfoPayload }>({
    mutationFn: ({ chatbotId, data }) => chatbotsService.updateBasicInfo(chatbotId, data),
  });
};

export const useSubmitDataCollection = () => {
  return useMutation<DataColletionResponse, Error, { chatbotId: string; data: DataColletionPayload }>({
    mutationFn: ({ chatbotId, data }) => chatbotsService.submitDataColletion(chatbotId, data),
  });
};

export const useUpdateConfig = () => {
  return useMutation<UpdateConfigResponse, Error, { chatbotId: string; data: UpdateConfigPayload }>({
    mutationFn: ({ chatbotId, data }) => chatbotsService.updateConfig(chatbotId, data),
  });
};

export const useUpdateAppearance = () => {
  return useMutation<UpdateAppearanceResponse, Error, { chatbotId: string; data: UpdateAppearancePayload }>({
    mutationFn: ({ chatbotId, data }) => chatbotsService.updateAppearance(chatbotId, data),
  });
};

export const useCreateDataField = () => {
  return useMutation<CreateDataFieldResponse, Error, { chatbotId: string; data: DataFieldCreatePayload }>({
    mutationFn: ({ chatbotId, data }) => chatbotsService.createDataField(chatbotId, data),
  });
};

export const useGetDataFields = (chatbotId?: string) => {
  try {
    return useQuery<GetDataFieldsResponse, Error>({
      queryKey: ['dataFields', chatbotId],
      queryFn: () => chatbotsService.getDataFields(chatbotId!),
      enabled: Boolean(chatbotId),
    });
  } catch (error) {
    throw error
  }
};

export const useUpdateDataField = () => {
  return useMutation<UpdateDataFieldResponse, Error, { chatbotId: string; fieldId: string; data: DataFieldUpdatePayload }>({
    mutationFn: ({ chatbotId, fieldId, data }) => chatbotsService.updateDataField(chatbotId, fieldId, data),
  });
};

export const useDeleteDataField = () => {
  return useMutation<DeleteDataFieldResponse, Error, { chatbotId: string; fieldId: string }>({
    mutationFn: ({ chatbotId, fieldId }) => chatbotsService.deleteDataField(chatbotId, fieldId),
  });
};

export const useUploadLogo = () => {
  return useMutation<UploadLogoResponse, Error, { chatbotId: string; file: File }>({
    mutationFn: ({ chatbotId, file }) => chatbotsService.uploadLogo(chatbotId, file),
  });
};